import {ThemeIconKey, Icon, Show, Hide, Center, getSize, Severity} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import RcUpload from 'rc-upload';
import {
  Action,
  RcFile,
  UploadProps as RcUploadProps,
  UploadRequestMethod,
  UploadRequestOption,
} from 'rc-upload/es/interface';
import {match} from 'ts-pattern';

import {useEffect, useState, useRef, KeyboardEvent} from 'react';

import {always, defaultTo, map, not, split, trim} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {Button} from '../Button/Button';
import {IconButton, IconButtonPriority} from '../IconButton/IconButton';
import {ProgressBar} from '../ProgressBar/ProgressBar';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';
import {UploadedFileFormatIsNotAllowedBySpecifiedAcceptedField} from './UploadedFileFormatIsNotAllowedBySpecifiedAcceptedField';
import {UploadState} from './UploadState';

export type UploadType = 'button' | 'card' | 'iconButton';
export type UploadButtonVariant = 'secondary' | 'ghostLink' | 'errorLink';
export type OnUploadError = NonNullable<RcUploadProps['onError']>;
export type OnUploadSuccess = NonNullable<RcUploadProps['onSuccess']>;
export type OnBeforeUpload = NonNullable<RcUploadProps['beforeUpload']>;
export type CustomRequestOption = UploadRequestOption;

export type File = {
  name: string;
  status: UploadState;
  url: string;
};

export interface UploadProps<T extends UploadType = UploadType> extends TestIdProps {
  /**
   * @about
   * Uploading URL
   */
  action?: Action;
  /**
   * @about
   * Request method
   */
  method?: UploadRequestMethod;
  /**
   * @about
   * File types that can be accepted
   */
  accept: string;
  /**
   * @about
   * If set true, component will validate file types according to accept property
   * before upload, and if file is not of appropriate file type, it will reject it
   * */
  shouldValidateAccept?: boolean;
  /**
   * @about
   * Whether to support selected multiple file.
   */
  isMultiple?: boolean;
  /**
   * @about
   * The file upload will be disabled.
   */
  isDisabled?: boolean;
  /**
   * @about
   * The current state of upload ("Idle" | "Uploading" | "Success" | "Error").
   */
  uploadState?: UploadState;
  /**
   * @about
   * The type of upload ("button" | "card").
   */
  type?: T;
  uploadText?: string;
  uploadingText?: string;
  name?: string;
  size?: 'default' | 'minHeight';
  uploadIcon?: ThemeIconKey;
  uploadingIcon?: ThemeIconKey;
  errorIcon?: ThemeIconKey;
  buttonVariant?: UploadButtonVariant;
  /**
   * @about
   * Provide an override for the default xhr behavior for additional customization
   */
  customRequest?: (option: CustomRequestOption) => void;
  onStart?: (file: RcFile) => void;
  onError?: OnUploadError;
  onSuccess?: OnUploadSuccess;
  beforeUpload?: OnBeforeUpload;
}

export function Upload<T extends UploadType = 'card'>(props: UploadProps<T>) {
  const t = useTranslationContext();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const uploadTriggerRef = useRef<HTMLDivElement>(null);

  const [internalUploadState, setInternalUploadState] = useState<UploadState>(
    props.uploadState ?? UploadState.Idle
  );
  const [previousUploadState, setPreviousUploadState] = useState(props.uploadState);
  const [isHovering, setIsHovering] = useState<boolean>(false);

  if (props.uploadState !== previousUploadState) {
    setPreviousUploadState(props.uploadState);
    if (props.uploadState) {
      setInternalUploadState(props.uploadState);
    }
  }

  // Manually set tabindex to -1 on the RcUpload DOM element, because RcUpload does not support it
  useEffect(function removeUploadButtonFromTabOrder() {
    const timeoutId = setTimeout(() => {
      if (wrapperRef.current) {
        const spanElement = wrapperRef.current.querySelector('span[role="button"]');
        if (spanElement) {
          spanElement.setAttribute('tabindex', '-1');
        }
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, []);

  const isUploading = internalUploadState === UploadState.Uploading;
  const hasError = internalUploadState === UploadState.Error;

  const isCard = props.type === 'card';
  const isButton = props.type === 'button';
  const isIconButton = props.type === 'iconButton';

  const size = props.size ?? 'default';

  const uploadingIcon = defaultTo('action/watch_later', props.uploadingIcon);
  const uploadIcon = defaultTo('file/upload', props.uploadIcon);
  const errorIcon = defaultTo('alert/error', props.errorIcon);

  const uploadText = defaultTo(t('general.actions.upload'), props.uploadText);
  const uploadingText = defaultTo(t('general.labels.uploading'), props.uploadingText);

  const handleMouseEnter = () => setIsHovering(true);

  const handleMouseLeave = () => setIsHovering(false);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === ' ' && !props.isDisabled && !isUploading) {
      event.preventDefault();
      uploadTriggerRef.current?.click();
    }
  };

  const handleOnStart = (file: RcFile) => {
    setInternalUploadState(UploadState.Uploading);
    props.onStart?.(file);
  };

  const handleOnSuccess: RcUploadProps['onSuccess'] = (response, file, xhr) => {
    props.onSuccess?.(response, file, xhr);
    setInternalUploadState(UploadState.Success);
  };

  const handleOnError: RcUploadProps['onError'] = (error, ret, file) => {
    props.onError?.(error, ret, file);
    setInternalUploadState(UploadState.Error);
  };

  const handleValidateByAccept = (file: RcFile, files: RcFile[]) =>
    validateFileByAccept(props.accept, handleOnError, file)
      ? (props.beforeUpload?.(file, files) ?? file)
      : false;

  const beforeUpload = !props.shouldValidateAccept ? props.beforeUpload : handleValidateByAccept;

  return (
    <div
      className={cn(
        uploadWrapperVariants({
          isCard,
          isDisabled: props.isDisabled ?? false,
          size,
          hasError,
        })
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onDragEnter={handleMouseEnter}
      onDragLeave={handleMouseLeave}
      ref={wrapperRef}
      data-testid={suffixTestId('uploadWrapper', props)}
    >
      <RcUpload
        // We need to use style prop here to be able to style the upload component properly
        style={
          isCard
            ? {
                width: '100%',
                height: '100%',
                minHeight: size === 'minHeight' ? getSize(27) : 'initial',
                minWidth: size === 'minHeight' ? getSize(27) : 'initial',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: size === 'default' ? getSize(2) : '0',
                cursor: props.isDisabled ? 'not-allowed' : 'pointer',
              }
            : {}
        }
        action={props.action}
        method={props.method}
        accept={props.accept}
        multiple={props.isMultiple}
        customRequest={props.customRequest}
        onStart={handleOnStart}
        onSuccess={handleOnSuccess}
        onError={handleOnError}
        beforeUpload={beforeUpload}
        disabled={props.isDisabled}
        data-testid={suffixTestId('rcUpload', props)}
      >
        <Show when={isButton}>
          <Button
            variant={props.buttonVariant}
            leftIcon={isUploading ? uploadingIcon : uploadIcon}
            isDisabled={props.isDisabled || isUploading}
            data-testid={suffixTestId('uploadButton', props)}
            title={isUploading ? uploadingText : uploadText}
          />
        </Show>
        <Show when={isIconButton}>
          <IconButton
            icon={isUploading ? uploadingIcon : uploadIcon}
            priority={match<UploadButtonVariant | undefined, IconButtonPriority>(
              props.buttonVariant
            )
              .with('secondary', always('secondary'))
              .with('ghostLink', always('default'))
              .with('errorLink', always('default'))
              .otherwise(always('primary'))}
            severity={match<UploadButtonVariant | undefined, Severity | undefined>(
              props.buttonVariant
            )
              .with('secondary', always(undefined))
              .with('ghostLink', always('informational'))
              .with('errorLink', always('danger'))
              .otherwise(always('informational'))}
            isDisabled={props.isDisabled || isUploading}
            data-testid={suffixTestId('uploadIconButton', props)}
          />
        </Show>
        <Hide when={isButton || isIconButton}>
          <Show when={isUploading}>
            <div className={cn(uploadingContainerVariants({size}))}>
              <ProgressBar indeterminate small />
            </div>
          </Show>
          <Show when={hasError && not(isHovering)}>
            <Center width="100%" height="100%" justify="center">
              <Icon value={errorIcon} color="severity.danger" size={6} />
            </Center>
          </Show>
          <Hide when={isUploading || (hasError && not(isHovering))}>
            <div
              ref={uploadTriggerRef}
              className={cn(uploadTriggerVariants())}
              // Inline style is needed to override cursor set by global [role="button"] selector
              style={{cursor: props.isDisabled ? 'not-allowed' : 'pointer'}}
              tabIndex={props.isDisabled ? -1 : 0}
              onKeyDown={handleKeyDown}
              role="button"
              aria-label={uploadText}
              aria-disabled={props.isDisabled}
            >
              <Icon
                value={uploadIcon}
                size={6}
                {...(hasError && {color: 'severity.danger'})}
                cursor={props.isDisabled ? 'not-allowed' : 'pointer'}
              />
            </div>
          </Hide>
        </Hide>
      </RcUpload>
    </div>
  );
}

const uploadWrapperVariants = cva('', {
  variants: {
    isCard: {
      true: 'h-full w-full rounded-[var(--radius-x-small)] border border-dashed border-[var(--color-palettes-neutral-80-100)] bg-[var(--color-palettes-white-10-100)] text-[length:var(--text-size-x-small)] font-[var(--font-weight-regular)] leading-[var(--text-leading-x-small)]',
      false: '',
    },
    size: {
      default: '',
      minHeight: '',
    },
    isDisabled: {
      true: 'border-[var(--color-palettes-neutral-80-40)] text-[var(--color-palettes-neutral-80-40)]',
      false: '',
    },
    hasError: {
      true: 'border-solid border-[var(--color-palettes-red-60-100)]',
      false: '',
    },
  },
  compoundVariants: [
    {
      isCard: true,
      size: 'minHeight',
      className:
        'flex min-h-[calc(var(--eag-space-step,0.25)*27rem)] min-w-[calc(var(--eag-space-step,0.25)*27rem)] flex-col items-center justify-center',
    },
    {
      isCard: true,
      isDisabled: false,
      className:
        'hover:border-[var(--color-palettes-blue-60-100)] hover:bg-[var(--color-palettes-blue-10-100)] hover:text-[var(--color-palettes-blue-80-100)]',
    },
  ],
});

const uploadingContainerVariants = cva('flex h-full w-full flex-col items-center justify-center', {
  variants: {
    size: {
      default: 'px-0 py-[10px]',
      minHeight: 'px-[10px] py-0',
    },
  },
});

const uploadTriggerVariants = cva('flex h-full w-full items-center justify-center');

const validateFileByAccept = (
  accept: string | undefined,
  onError: OnUploadError | undefined,
  file: RcFile
) => {
  if (!accept) {
    return file;
  }

  const acceptedFileTypes = map(trim, split(',', accept));
  if (acceptedFileTypes.includes(file.type)) {
    return file;
  }

  onError?.(new UploadedFileFormatIsNotAllowedBySpecifiedAcceptedField(), {}, file);

  // Stops upload.
  return false;
};
